import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import HelmetWrapper from "../../HelmetWrapper";
import CommonStructured from "../../components/common-components/CommonStructured";
import SelectDropdown from "../../components/components-react/SelectDropdown";
import { selectUser } from "../../components/auth/auth.selector";
import { selectGymData } from "../../redux/reducers/gymDataSlice";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import { fetchGymData } from "../../data/APIs";
import "../../styles/essl/essl-member-dump.css";

const ESSLMemberDump = () => {
  const dispatch = useDispatch();
  const userInfoData = useSelector(selectUser);
  const gymData = useSelector(selectGymData);
  const { postPutLoading } = useSelector((state) => state.common);

  const iniFormValues = {
    selectedGym: "",
    page: "1",
    location: "",
    limit: "",
    mobileNumber: "",
  };

  const [formValues, setFormValues] = useState(iniFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  const validateForm = (values) => {
    const errors = {};

    if (!values.selectedGym) {
      errors.selectedGym = "Please select a gym";
    }

    if (!values.page) {
      errors.page = "Please enter page number";
    } else if (values.page <= 0) {
      errors.page = "Page must be greater than 0";
    }

    if (!values.location || values.location.trim() === "") {
      errors.location = "Please enter location";
    }

    if (!values.limit) {
      errors.limit = "Please enter limit";
    } else if (values.limit <= 0) {
      errors.limit = "Limit must be greater than 0";
    }

    if (values.mobileNumber && values.mobileNumber.length !== 10) {
      errors.mobileNumber = "Mobile number must be 10 digits";
    }

    return errors;
  };

  const onChangeInput = (e) => {
    const { name, value } = e.target;
    setIsSubmit(false);

    if (name === "mobileNumber") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setFormValues((prevs) => ({
        ...prevs,
        [name]: numericValue,
      }));
    } else if (name === "limit" || name === "page") {
      const numericValue = value.replace(/\D/g, "");
      setFormValues((prevs) => ({
        ...prevs,
        [name]: numericValue,
      }));
    } else {
      setFormValues((prevs) => ({
        ...prevs,
        [name]: value,
      }));
    }
  };

  const onChangeDropdown = (value, name) => {
    setIsSubmit(false);
    setFormValues((prevs) => ({
      ...prevs,
      [name]: value,
    }));
  };

  const handleResetForm = () => {
    setFormValues(iniFormValues);
    setFormErrors({});
    setIsSubmit(false);
    dispatch(fetchGymData(userInfoData?.token));
  };

  const handleSubmitForm = async () => {
    const method = "POST";
    const endpoint = "/essl/users";
    const token = userInfoData?.token || "";

    const payload = {
      gym_id: formValues.selectedGym?.value || "",
      page: parseInt(formValues.page) || 1,
      limit: parseInt(formValues.limit),
      location: formValues.location.trim(),
      ...(formValues.mobileNumber && { mobile: formValues.mobileNumber }),
    };

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        setIsSubmit(false);
        toast.success(
          result?.payload?.message || "ESSL Member Dump Created Successfully!"
        );
        handleResetForm();
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error making POST request:", error);
      toast.error("Failed to submit form. Please try again.");
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm(formValues);
    setFormErrors(errors);
    setIsSubmit(true);
  };

  useEffect(() => {
    dispatch(fetchGymData(userInfoData?.token));
  }, []);

  useEffect(() => {
    if (isSubmit && Object.keys(formErrors).length === 0) {
      handleSubmitForm();
    }
  }, [isSubmit, formErrors]);

  return (
    <HelmetWrapper title="WTF: ESSL Member Dump">
      <Container
        className="bg-cloud-k px-50px gobal-container esslMemberDumpContainer"
        fluid
      >
        <Row>
          <Col>
            <CommonStructured
              pageHeading="ESSL Member Dump"
              isReload={handleResetForm}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="esslMemberDumpFormWrapper">
              <form onSubmit={onSubmit} className="esslMemberDumpForm">
                <div className="esslMemberDumpFormGrid">
                  <div className="esslMemberDumpFormInputBox">
                    <label
                      htmlFor="selectedGym"
                      className="esslMemberDumpFormInputLabel"
                    >
                      Gym Name <span className="esslMemberDumpRequired">*</span>
                    </label>

                    <div
                      className={`esslMemberDumpCommonWrapper ${
                        formErrors.selectedGym ? "esslMemberDumpFormError" : ""
                      }`}
                    >
                      <SelectDropdown
                        options={gymData?.length > 0 ? gymData : []}
                        selectedOption={formValues.selectedGym}
                        setSelectedOption={(selectedValue) =>
                          onChangeDropdown(selectedValue, "selectedGym")
                        }
                        name="Gym"
                      />
                    </div>

                    <span className="esslMemberDumpErrorLabel">
                      {formErrors?.selectedGym || ""}
                    </span>
                  </div>

                  <div className="esslMemberDumpFormInputBox">
                    <label
                      htmlFor="location"
                      className="esslMemberDumpFormInputLabel"
                    >
                      Location <span className="esslMemberDumpRequired">*</span>
                    </label>

                    <input
                      type="text"
                      name="location"
                      placeholder="Enter Location"
                      className={`esslMemberDumpFormInput ${
                        formErrors.location ? "esslMemberDumpFormError" : ""
                      }`}
                      value={formValues.location}
                      onChange={onChangeInput}
                    />

                    <span className="esslMemberDumpErrorLabel">
                      {formErrors?.location || ""}
                    </span>
                  </div>

                  <div className="esslMemberDumpFormInputBox">
                    <label
                      htmlFor="page"
                      className="esslMemberDumpFormInputLabel"
                    >
                      Page <span className="esslMemberDumpRequired">*</span>
                    </label>

                    <input
                      type="number"
                      name="page"
                      placeholder="Enter Page Number"
                      className={`esslMemberDumpFormInput ${
                        formErrors.page ? "esslMemberDumpFormError" : ""
                      }`}
                      value={formValues.page}
                      onChange={onChangeInput}
                      min="1"
                    />

                    <span className="esslMemberDumpErrorLabel">
                      {formErrors?.page || ""}
                    </span>
                  </div>

                  <div className="esslMemberDumpFormInputBox">
                    <label
                      htmlFor="limit"
                      className="esslMemberDumpFormInputLabel"
                    >
                      Limit <span className="esslMemberDumpRequired">*</span>
                    </label>

                    <input
                      type="number"
                      name="limit"
                      placeholder="Enter Limit"
                      className={`esslMemberDumpFormInput ${
                        formErrors.limit ? "esslMemberDumpFormError" : ""
                      }`}
                      value={formValues.limit}
                      onChange={onChangeInput}
                      min="1"
                    />

                    <span className="esslMemberDumpErrorLabel">
                      {formErrors?.limit || ""}
                    </span>
                  </div>

                  <div className="esslMemberDumpFormInputBox">
                    <label
                      htmlFor="mobileNumber"
                      className="esslMemberDumpFormInputLabel"
                    >
                      Mobile Number{" "}
                      <span className="esslMemberDumpOptional">(Optional)</span>
                    </label>

                    <input
                      type="text"
                      name="mobileNumber"
                      placeholder="Enter Mobile Number"
                      className={`esslMemberDumpFormInput ${
                        formErrors.mobileNumber ? "esslMemberDumpFormError" : ""
                      }`}
                      value={formValues.mobileNumber}
                      onChange={onChangeInput}
                      maxLength="10"
                    />

                    <span className="esslMemberDumpErrorLabel">
                      {formErrors?.mobileNumber || ""}
                    </span>
                  </div>
                </div>

                <div className="esslMemberDumpFormButtonsContainer">
                  <button
                    type="button"
                    className="esslMemberDumpFormButton esslMemberDumpFormButtonReset"
                    onClick={handleResetForm}
                    disabled={postPutLoading}
                  >
                    Reset
                  </button>

                  <button
                    type="submit"
                    className="esslMemberDumpFormButton esslMemberDumpFormButtonSubmit"
                    disabled={postPutLoading}
                  >
                    {postPutLoading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default ESSLMemberDump;
